import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

// A small dependency-free WYSIWYG editor: bold / italic / underline, two
// heading levels, both list kinds, quote, link and "clear formatting".
//
// WHY NOT A LIBRARY. The only thing in the project that needs rich text is an
// announcement body, and the backend already refuses everything outside a
// short tag whitelist (see src/Announcements/AnnouncementContent.php). Pulling
// in Quill or TipTap would add a few hundred KB to every page load — including
// every page that has no editor on it — to produce markup we then throw most
// of away. contentEditable plus execCommand is deprecated but universally
// implemented, and it emits exactly the tags the whitelist allows.
//
// NO IMAGES, on purpose (Timotei: "no image support, for now"). There is no
// image button and the paste handler strips everything but plain text, so a
// paste from Word cannot smuggle in a base64 <img> or a wall of inline styles
// that the sanitizer would then have to argue with.
//
// The value flows ONE WAY into the DOM. Rewriting innerHTML on every keystroke
// would put the caret back at the start of the box after every character, so
// the node is seeded once and only re-seeded when the value changes from the
// OUTSIDE (a reset, or the language tab switching) — detected by comparing
// against what we last emitted.

const BUTTONS = [
  ['bold',          'b',  { cmd: 'bold' }],
  ['italic',        'i',  { cmd: 'italic' }],
  ['underline',     'u',  { cmd: 'underline' }],
  ['h3',            'H1', { cmd: 'formatBlock', arg: 'h3' }],
  ['h4',            'H2', { cmd: 'formatBlock', arg: 'h4' }],
  ['ul',            '••', { cmd: 'insertUnorderedList' }],
  ['ol',            '1.', { cmd: 'insertOrderedList' }],
  ['quote',         '❝',  { cmd: 'formatBlock', arg: 'blockquote' }],
]

export function RichText({ value, onChange, disabled = false, placeholder = '', id }) {
  const { t } = useTranslation()
  const ref = useRef(null)
  const lastEmitted = useRef(null)

  // Seed / re-seed only on an OUTSIDE change (see the note above).
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const incoming = value || ''
    if (incoming === lastEmitted.current) return
    el.innerHTML = incoming
    lastEmitted.current = incoming
  }, [value])

  function emit() {
    const el = ref.current
    if (!el) return
    // An "empty" contentEditable is <br> or <p><br></p> depending on the
    // browser; report it as empty so `required` behaves.
    const html = el.innerHTML
    const bare = html.replace(/<br\s*\/?>|&nbsp;|\s|<\/?(p|div)>/gi, '')
    const out = bare === '' ? '' : html
    lastEmitted.current = out
    onChange(out)
  }

  function run(cmd, arg) {
    if (disabled) return
    ref.current?.focus()
    // execCommand is deprecated but has no replacement that works everywhere;
    // the whole editor is 100 lines precisely because of it.
    document.execCommand(cmd, false, arg)
    emit()
  }

  function addLink() {
    if (disabled) return
    const url = window.prompt(t('announcements.editor.link_prompt'), 'https://')
    if (!url) return
    // Only http(s) and mailto — the sanitizer enforces the same rule server
    // side, but there is no reason to let a javascript: URL exist even for a
    // moment in the admin's own browser.
    if (!/^(https?:|mailto:)/i.test(url)) return
    run('createLink', url)
  }

  // Paste as PLAIN TEXT. Anything else drags Word's inline styles, class
  // names and <img> tags into a body the sanitizer would strip anyway — the
  // admin would then be editing markup that does not survive the save.
  function onPaste(e) {
    e.preventDefault()
    const text = (e.clipboardData || window.clipboardData).getData('text/plain')
    document.execCommand('insertText', false, text)
    emit()
  }

  const empty = !value || value === ''

  return (
    <div className={`richtext${disabled ? ' is-disabled' : ''}`}>
      <div className="richtext-toolbar" role="toolbar">
        {BUTTONS.map(([key, label, { cmd, arg }]) => (
          <button
            key={key}
            type="button"
            className="richtext-btn"
            disabled={disabled}
            title={t(`announcements.editor.${key}`)}
            aria-label={t(`announcements.editor.${key}`)}
            // Keep the selection: a plain click moves focus out of the box
            // first, and execCommand then has nothing to format.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => run(cmd, arg)}
          >
            {label}
          </button>
        ))}
        <button
          type="button" className="richtext-btn" disabled={disabled}
          title={t('announcements.editor.link')} aria-label={t('announcements.editor.link')}
          onMouseDown={(e) => e.preventDefault()} onClick={addLink}
        >🔗</button>
        <button
          type="button" className="richtext-btn" disabled={disabled}
          title={t('announcements.editor.clear')} aria-label={t('announcements.editor.clear')}
          onMouseDown={(e) => e.preventDefault()} onClick={() => run('removeFormat')}
        >✕</button>
      </div>

      <div
        id={id}
        ref={ref}
        className="richtext-area"
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        data-empty={empty ? '1' : '0'}
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
      />
    </div>
  )
}
